import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { IdParamsType } from 'utils/types';
import { PunchupActionButton } from 'features/common/components/Punchup/primitives/buttons/PunchupActionButton';
import { PunchupColumnPicker } from 'features/common/components/Punchup/composites/tables/PunchupColumnPicker';
import { PunchupTablePage } from 'features/common/components/Punchup/pages/PunchupTablePage';
import { PunchupSearch } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupSearch';
import { PunchupErrorState } from 'features/common/components/Punchup/primitives/feedback/PunchupErrorState';
import { isBackgroundRefetch } from 'features/common/utils/queryStatus';
import { PunchupTablePageHeader } from 'features/common/components/Punchup/composites/tables/PunchupTablePageHeader';
import { PunchupRunLotteryModal } from './PunchupRunLotteryModal';
import { LOTTERY_COLUMN_DEFS } from './PunchupLotteryTable/constants';
import { PunchupLotteryTable } from './PunchupLotteryTable';
import { getData } from 'services/api';
import { handleDownload } from 'utils/handleLink';
import { postToast } from 'features/common/utils/postFlash';
import { useColumnSelection } from 'features/common/hooks/useColumnSelection';
import { useDebounce } from 'utils/utils';
import { useEventLotteryEntries } from 'features/LotteryEntries/hooks/useEventLotteryEntries';

const PAGE_NAME = 'Lottery Entries';

export const PunchupLotteryEntries: FC = () => {
	const { t } = useTranslation();
	const { id } = useParams<IdParamsType>();
	const { entriesQueryObject, counts, setSearchText } = useEventLotteryEntries();
	const { visibleDefs, visibleKeys, gridColumns, toggleColumn, allDefs } = useColumnSelection(
		LOTTERY_COLUMN_DEFS,
		true
	);
	const searchDebounce = useDebounce(setSearchText);

	const [isRunLotteryOpen, setIsRunLotteryOpen] = useState(false);
	const [isDownloading, setIsDownloading] = useState(false);

	const hasAnyEntries =
		!!counts &&
		counts.pending +
			counts.winner +
			counts.not_winner +
			counts.pending_wheelchair +
			counts.winner_wheelchair +
			counts.not_winner_wheelchair >
			0;
	const runLotteryDisabled = !hasAnyEntries;

	// On a failed FIRST load react-query leaves `data` undefined, so the table body would otherwise show
	// the "No lottery entries found" empty state — surface a persistent, retryable error panel instead. A
	// background-refetch error (data already present) keeps the stale table + the toast below.
	const errorFallback =
		entriesQueryObject.isError && !entriesQueryObject.data ? (
			<PunchupErrorState
				title={t('lottery-load-error-title', "We couldn't load lottery entries")}
				description={t('lottery-load-error-description', 'Something went wrong fetching the entries for this show.')}
				onRetry={() => entriesQueryObject.refetch()}
				retryLabel={t('button-try-again', 'Try again')}
			/>
		) : undefined;

	useEffect(() => {
		if (entriesQueryObject.isError) postToast('error', t('lottery-load-error', 'Failed to load lottery entries'));
	}, [entriesQueryObject.isError, t]);

	const handleDownloadCsv = async () => {
		setIsDownloading(true);
		try {
			const response = await getData(`/events/${id}/lottery/entries/csv`);
			handleDownload(response, 'entries.csv');
		} catch {
			postToast('error', t('lottery-csv-error', 'Error downloading CSV'));
		} finally {
			setIsDownloading(false);
		}
	};

	return (
		<PunchupTablePage
			pageName={PAGE_NAME}
			header={
				<PunchupTablePageHeader
					title={PAGE_NAME}
					description={t('lottery-entries-description', 'Review entries and run the lottery to select winners.')}
					customAction={
						<PunchupActionButton onClick={() => setIsRunLotteryOpen(true)} disabled={runLotteryDisabled}>
							{t('lottery-run-lottery', 'Run Lottery')}
						</PunchupActionButton>
					}
					onDownload={handleDownloadCsv}
					downloadLabel={t('button-download', 'Download')}
					isDownloading={isDownloading}
					onRefresh={() => entriesQueryObject.refetch()}
					refreshLabel={t('button-refresh', 'Refresh')}
					isRefreshing={entriesQueryObject.isFetching}
				/>
			}
			search={
				<PunchupSearch onChange={(e) => searchDebounce(e.target.value)} placeholder={t('search-email-or-name')} />
			}
			controlsActions={<PunchupColumnPicker allDefs={allDefs} visibleKeys={visibleKeys} onToggle={toggleColumn} />}
			pageFallback={errorFallback}
			overlays={
				isRunLotteryOpen && (
					<PunchupRunLotteryModal
						showModal={isRunLotteryOpen}
						closeModal={() => setIsRunLotteryOpen(false)}
						refetch={entriesQueryObject.refetch}
						counts={counts}
					/>
				)
			}
		>
			<PunchupLotteryTable
				entriesQueryObject={entriesQueryObject}
				visibleDefs={visibleDefs}
				gridColumns={gridColumns}
				isRefetching={isBackgroundRefetch(entriesQueryObject)}
			/>
		</PunchupTablePage>
	);
};
